import { useState, useEffect } from 'react';
import DashboardLayout from '../dashboard/components/DashboardLayout';
import { RevenueStats } from './components/RevenueStats';
import { FinancialChart } from './components/FinancialChart';
import { ExpenseList } from './components/ExpenseList';
import { ExpenseForm } from './components/ExpenseForm';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface Expense {
  id: string;
  name: string;
  amount: number;
  category: string;
  date: string;
  description?: string;
  createdAt: string;
}

interface Revenue {
  date: string;
  amount: number;
  source: 'orders' | 'other';
}

interface PartnerStats {
  totalRevenue: number;
  totalCommission: number;
  orderCount: number;
  monthlyData: Array<{
    month: string;
    revenue: number;
    commission: number;
  }>;
}

export default function Reports() {
  const { userProfile } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [revenues, setRevenues] = useState<Revenue[]>([]);
  const [partnerStats, setPartnerStats] = useState<PartnerStats>({
    totalRevenue: 0,
    totalCommission: 0,
    orderCount: 0,
    monthlyData: []
  });
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingMockData, setUsingMockData] = useState(false);

  // Load partner-specific statistics
  const loadPartnerStats = async () => {
    if (!userProfile?.email) return;

    try {
      setLoading(true);
      setError(null);

      // Get partner's orders
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('total_amount, created_at, status')
        .eq('partner_email', userProfile.email)
        .eq('status', 'delivered')
        .order('created_at', { ascending: false });

      if (ordersError) {
        console.warn('Lỗi kết nối database orders:', ordersError.message);
        setPartnerStats({
          totalRevenue: 0,
          totalCommission: 0,
          orderCount: 0,
          monthlyData: []
        });
        return;
      }

      if (!orders || orders.length === 0) {
        console.log('Chưa có đơn hàng nào');
        setPartnerStats({
          totalRevenue: 0,
          totalCommission: 0,
          orderCount: 0,
          monthlyData: []
        });
        return;
      }

      // Calculate statistics
      const totalRevenue = orders.reduce((sum, order) => sum + order.total_amount, 0);
      const commissionRate = userProfile.commission_rate || 15;
      const totalCommission = totalRevenue * (commissionRate / 100);
      const orderCount = orders.length;

      // Group by month for chart
      const monthlyData: { [key: string]: { revenue: number; commission: number } } = {};
      
      orders.forEach(order => {
        const month = order.created_at.split('T')[0].substring(0, 7); // YYYY-MM
        if (!monthlyData[month]) {
          monthlyData[month] = { revenue: 0, commission: 0 };
        }
        monthlyData[month].revenue += order.total_amount;
        monthlyData[month].commission += order.total_amount * (commissionRate / 100);
      });

      const monthlyArray = Object.entries(monthlyData)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, data]) => ({
          month,
          revenue: data.revenue,
          commission: data.commission
        }));

      setPartnerStats({
        totalRevenue,
        totalCommission,
        orderCount,
        monthlyData: monthlyArray
      });

    } catch (err: any) {
      console.warn('Lỗi kết nối database:', err.message);
      setPartnerStats({
        totalRevenue: 0,
        totalCommission: 0,
        orderCount: 0,
        monthlyData: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Load expenses from Supabase (only for manager/admin)
  const loadExpenses = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: expenseError } = await supabase
        .from('expenses')
        .select('id, name, amount, category, date, description, created_at')
        .order('date', { ascending: false });

      if (expenseError) {
        console.warn('Lỗi kết nối database expenses:', expenseError.message);
        setExpenses([]);
        setUsingMockData(true);
        return;
      }

      if (!data || data.length === 0) {
        console.log('Chưa có dữ liệu expenses');
        setExpenses([]);
        setUsingMockData(false);
        return;
      }

      const transformedExpenses: Expense[] = data.map(expense => ({
        id: expense.id,
        name: expense.name,
        amount: expense.amount,
        category: expense.category,
        date: expense.date,
        description: expense.description,
        createdAt: expense.created_at
      }));

      setExpenses(transformedExpenses);
      setUsingMockData(false);
    } catch (err: any) {
      console.warn('Lỗi kết nối database expenses:', err.message);
      setExpenses([]);
      setUsingMockData(true);
    }
  };

  // Load revenues from orders (only for manager/admin)
  const loadRevenues = async () => {
    try {
      const { data, error: revenueError } = await supabase
        .from('orders')
        .select('total_amount, created_at, status')
        .eq('status', 'delivered')
        .order('created_at', { ascending: false });

      if (revenueError) {
        console.warn('Lỗi kết nối database orders:', revenueError.message);
        setRevenues([]);
        return;
      }

      if (!data || data.length === 0) {
        console.log('Chưa có dữ liệu orders');
        setRevenues([]);
        return;
      }

      const transformedRevenues: Revenue[] = data.map(order => ({
        date: order.created_at.split('T')[0],
        amount: order.total_amount,
        source: 'orders' as const
      }));

      setRevenues(transformedRevenues);
    } catch (err: any) {
      console.warn('Lỗi kết nối database orders:', err.message);
      setRevenues([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.role === 'partner') {
      loadPartnerStats();
    } else {
      Promise.all([loadExpenses(), loadRevenues()]);
    }
  }, [userProfile]);

  // ... existing expense management functions (only for manager/admin) ...
  const handleCreateExpense = async (expenseData: Omit<Expense, 'id' | 'createdAt'>) => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('expenses')
        .insert({
          name: expenseData.name,
          amount: expenseData.amount,
          category: expenseData.category,
          date: expenseData.date,
          description: expenseData.description
        })
        .select()
        .single();

      if (error) {
        console.warn('Lỗi tạo expense trong database, thêm vào local:', error.message);
        const newExpense: Expense = {
          id: Date.now().toString(),
          ...expenseData,
          createdAt: new Date().toISOString()
        };
        setExpenses([newExpense, ...expenses]);
        setShowExpenseForm(false);
        return;
      }

      const newExpense: Expense = {
        id: data.id,
        name: data.name,
        amount: data.amount,
        category: data.category,
        date: data.date,
        description: data.description,
        createdAt: data.created_at
      };

      setExpenses([newExpense, ...expenses]);
      setShowExpenseForm(false);
    } catch (err: any) {
      console.warn('Lỗi tạo expense, thêm vào local:', err.message);
      const newExpense: Expense = {
        id: Date.now().toString(),
        ...expenseData,
        createdAt: new Date().toISOString()
      };
      setExpenses([newExpense, ...expenses]);
      setShowExpenseForm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateExpense = async (expenseId: string, updates: Partial<Expense>) => {
    try {
      setLoading(true);

      const { error } = await supabase
        .from('expenses')
        .update({
          name: updates.name,
          amount: updates.amount,
          category: updates.category,
          date: updates.date,
          description: updates.description
        })
        .eq('id', expenseId);

      if (error) {
        console.warn('Lỗi cập nhật expense trong database, cập nhật local:', error.message);
      }

      setExpenses(expenses.map(expense => 
        expense.id === expenseId 
          ? { ...expense, ...updates }
          : expense
      ));
      setEditingExpense(null);
    } catch (err: any) {
      console.warn('Lỗi cập nhật expense, cập nhật local:', err.message);
      setExpenses(expenses.map(expense => 
        expense.id === expenseId 
          ? { ...expense, ...updates }
          : expense
      ));
      setEditingExpense(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      setLoading(true);

      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', expenseId);

      if (error) {
        console.warn('Lỗi xóa expense trong database, xóa khỏi local:', error.message);
      }

      setExpenses(expenses.filter(expense => expense.id !== expenseId));
    } catch (err: any) {
      console.warn('Lỗi xóa expense, xóa khỏi local:', err.message);
      setExpenses(expenses.filter(expense => expense.id !== expenseId));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllExpenses = async () => {
    try {
      setLoading(true);

      const { error } = await supabase
        .from('expenses')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');

      if (error) {
        console.warn('Lỗi xóa tất cả expenses trong database, xóa khỏi local:', error.message);
      }

      setExpenses([]);
    } catch (err: any) {
      console.warn('Lỗi xóa tất cả expenses, xóa khỏi local:', err.message);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics for manager/admin
  const totalRevenue = revenues.reduce((sum, revenue) => sum + revenue.amount, 0);
  const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const netProfit = totalRevenue - totalExpenses;

  // Group data by month for chart (manager/admin)
  const getMonthlyData = () => {
    const monthlyData: { [key: string]: { revenue: number; expenses: number } } = {};

    revenues.forEach(revenue => {
      const month = revenue.date.substring(0, 7);
      if (!monthlyData[month]) {
        monthlyData[month] = { revenue: 0, expenses: 0 };
      }
      monthlyData[month].revenue += revenue.amount;
    });

    expenses.forEach(expense => {
      const month = expense.date.substring(0, 7);
      if (!monthlyData[month]) {
        monthlyData[month] = { revenue: 0, expenses: 0 };
      }
      monthlyData[month].expenses += expense.amount;
    });

    return Object.entries(monthlyData)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, data]) => ({
        month,
        revenue: data.revenue,
        expenses: data.expenses,
        profit: data.revenue - data.expenses
      }));
  };

  if (loading && (userProfile?.role === 'partner' ? partnerStats.orderCount === 0 : expenses.length === 0 && revenues.length === 0)) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-8 sm:py-12">
          <div className="text-center">
            <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-3 sm:mb-4"></div>
            <p className="text-sm sm:text-base text-gray-600">Đang tải dữ liệu...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Partner view
  if (userProfile?.role === 'partner') {
    return (
      <DashboardLayout>
        <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 lg:px-0">
          {/* Header Section */}
          <div className="space-y-3 sm:space-y-4">
            <div className="space-y-2 sm:space-y-3">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Báo cáo đối tác</h1>
              <p className="text-sm sm:text-base text-gray-600">
                Theo dõi doanh thu và hoa hồng của bạn
              </p>
              {partnerStats.orderCount === 0 && (
                <div className="text-xs sm:text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg inline-block">
                  <i className="ri-information-line mr-1"></i>
                  Chưa có đơn hàng nào. Hãy bắt đầu bán hàng để xem báo cáo!
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-500 mr-2 flex-shrink-0"></i>
                <span className="text-red-700 flex-1 text-sm sm:text-base">{error}</span>
                <button
                  onClick={() => setError(null)}
                  className="ml-2 text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>
          )}

          {/* Partner Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            {/* Total Revenue */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 sm:p-6 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs sm:text-sm font-medium">Tổng doanh thu</p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold mt-1 sm:mt-2">
                    {partnerStats.totalRevenue.toLocaleString('vi-VN')}đ
                  </p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-400 bg-opacity-30 rounded-lg flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-lg sm:text-xl"></i>
                </div>
              </div>
            </div>

            {/* Total Commission */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 sm:p-6 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs sm:text-sm font-medium">Tổng hoa hồng</p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold mt-1 sm:mt-2">
                    {partnerStats.totalCommission.toLocaleString('vi-VN')}đ
                  </p>
                  <p className="text-green-200 text-xs mt-1">
                    ({userProfile.commission_rate || 15}% hoa hồng)
                  </p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-400 bg-opacity-30 rounded-lg flex items-center justify-center">
                  <i className="ri-hand-coin-line text-lg sm:text-xl"></i>
                </div>
              </div>
            </div>

            {/* Total Orders */}
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 sm:p-6 text-white shadow-lg sm:col-span-2 lg:col-span-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs sm:text-sm font-medium">Tổng đơn hàng</p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold mt-1 sm:mt-2">
                    {partnerStats.orderCount}
                  </p>
                  <p className="text-purple-200 text-xs mt-1">Đơn hàng đã giao</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-400 bg-opacity-30 rounded-lg flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-lg sm:text-xl"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Partner Chart */}
          {partnerStats.monthlyData.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
                Biểu đồ doanh thu và hoa hồng theo tháng
              </h3>
              <div className="space-y-4">
                {partnerStats.monthlyData.map((data, index) => (
                  <div key={index} className="border-b border-gray-100 pb-4 last:border-b-0">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-600">
                        Tháng {data.month}
                      </span>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900">
                          {data.revenue.toLocaleString('vi-VN')}đ
                        </div>
                        <div className="text-xs text-green-600">
                          Hoa hồng: {data.commission.toLocaleString('vi-VN')}đ
                        </div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ 
                          width: `${Math.min((data.revenue / Math.max(...partnerStats.monthlyData.map(d => d.revenue))) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // Manager/Admin view (existing functionality)
  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 lg:px-0">
        {/* Header Section */}
        <div className="space-y-3 sm:space-y-4">
          <div className="space-y-2 sm:space-y-3">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Báo cáo tài chính</h1>
            <p className="text-sm sm:text-base text-gray-600">
              Theo dõi doanh thu, chi phí và lợi nhuận
            </p>
            {expenses.length === 0 && revenues.length === 0 && (
              <div className="text-xs sm:text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg inline-block">
                <i className="ri-information-line mr-1"></i>
                Chưa có dữ liệu tài chính. Hãy bắt đầu thêm khoản chi đầu tiên!
              </div>
            )}
          </div>
          
          {/* Add Expense Button */}
          <button
            onClick={() => setShowExpenseForm(true)}
            className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap min-h-[44px]"
          >
            <i className="ri-add-circle-line text-lg sm:text-xl"></i>
            <span className="text-sm sm:text-base font-medium">Thêm khoản chi</span>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center">
              <i className="ri-error-warning-line text-red-500 mr-2 flex-shrink-0"></i>
              <span className="text-red-700 flex-1 text-sm sm:text-base">{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-2 text-red-500 hover:text-red-700 p-1 flex-shrink-0"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        )}

        {/* Revenue Stats */}
        <RevenueStats
          totalRevenue={totalRevenue}
          totalExpenses={totalExpenses}
          netProfit={netProfit}
          orderCount={revenues.length}
        />

        {/* Financial Chart */}
        <FinancialChart data={getMonthlyData()} />

        {/* Expense Form Modal */}
        {showExpenseForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <ExpenseForm
                onSubmit={handleCreateExpense}
                onCancel={() => setShowExpenseForm(false)}
              />
            </div>
          </div>
        )}

        {/* Edit Expense Form Modal */}
        {editingExpense && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <ExpenseForm
                expense={editingExpense}
                onSubmit={(data) => handleUpdateExpense(editingExpense.id, data)}
                onCancel={() => setEditingExpense(null)}
                isEditing={true}
              />
            </div>
          </div>
        )}

        {/* Expense List */}
        <ExpenseList
          expenses={expenses}
          onEdit={setEditingExpense}
          onDelete={handleDeleteExpense}
          onDeleteAll={handleDeleteAllExpenses}
          loading={loading}
        />
      </div>
    </DashboardLayout>
  );
}
